import { H1, H2 } from "../../../../components/text/headings";
import {
  useGetSupplierEngagementReport,
  useGetSupplierOverviewData,
} from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { YearDropdown } from "../components/FootPrint";
import * as React from "react";
import { useEffect, useState } from "react";
import { Badge, Box, Flex, Grid, GridItem, Heading, Tag, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { getFormattedNumValue, showErrorToast } from "../../../../utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../../config";
import { ArrowLeft, TrendDown, TrendUp } from "@phosphor-icons/react";
import { Page } from "../../../../components/Page";
import DashboardCard from "../../../ISSB/Dashboard/components/DashboardCard";
import LocalSpinner from "../../../../components/LocalSpinner";
import IndividualSupplierTrends from "./IndividualSupplierTrends";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";
import { useGetSupplierEngagementReports } from "../../../../hooks/query/reports";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "../../../SupplierQuestionnaire/helpers";
import moment from "moment";
import {
  getStatusLabel
} from "../../SupplierReports/components/SuppliersDataReport/components/SupplierEngagement/components/SupplierEngagementGrid";
import { SuppliersDataModal } from "../../SupplierReports/components/SuppliersDataReport/components";

const SupplierIndividualDetail = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get("supplierId");

  const [filters, setFilters] = useState();

  const [currentYearEngagementData, setCurrentYearEngagementData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);

  const [decarbTag, setDecarbTag] = useState("Decarbonisation");
  const [qTypeTag, setQTypeTag] = useState("N.A.");
  const [qStatusTag, setQStatusTag] = useState("N.A.");
  const [qQualityTag, setQQualityTag] = useState("N.A.");
  const [ghgId, setGhgId] = useState(null);
  const [decarbId, setDecarbId] = useState(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(null);

  const {
    isOpen: isSuppliersQuestionnaireOpen,
    onOpen: onSuppliersQuestionnaireOpen,
    onClose: onSuppliersQuestionnaireClose
  } = useDisclosure();

  const prepareData = (data) => {
    const scope1Total = data?.engagementReport?.revenue?.scope1TotalCo2Emissions || 0;
    const scope2Total = data?.engagementReport?.revenue?.scope2TotalCo2Emissions || 0;
    const scope3Total = data?.engagementReport?.revenue?.scope3TotalCo2Emissions || 0;
    const totalCo2Emissions = data?.engagementReport?.totalCo2Emissions || 0;

    const scope1Attributed = data?.engagementReport?.revenue?.scope1ApportionedCo2Emissions || 0;
    const scope2Attributed = data?.engagementReport?.revenue?.scope2ApportionedCo2Emissions || 0;
    const scope3Attributed = data?.engagementReport?.revenue?.scope3ApportionedCo2Emissions || 0;
    const totalAttributed = scope1Attributed + scope2Attributed + scope3Attributed;
    const year = data?.engagementReport?.year;
    const target = data?.yearlyEmission?.yearlyEmissionData?.[year]?.target || 0;
    let delta = target
      ? ((data?.yearlyEmission?.yearlyEmissionData?.[year]?.totalCo2Emissions - target) / target) * 100
      : 0;

    return {
      scope1Total: scope1Total,
      scope2Total: scope2Total,
      scope3Total: scope3Total,
      scope1And2Total: scope1Total + scope2Total,
      scope1Attributed: scope1Attributed,
      scope2Attributed: scope2Attributed,
      scope3Attributed: scope3Attributed,
      scope1And2Attributed: scope1Attributed + scope2Attributed,
      totalCo2Emissions: totalCo2Emissions,
      totalAttributed: totalAttributed,
      supplierName: data?.yearlyEmission?.supplierName || "Unknown Supplier",
      year: year,
      spend: data?.yearlyEmission?.yearlyEmissionData?.[year]?.spend || 0,
      intensity: data?.yearlyEmission?.yearlyEmissionData?.[year]?.intensity || 0,
      target: target,
      delta: delta,
      revenueSpend: data?.yearlyEmission?.yearlyEmissionData?.[year]?.revenueSpend || 0,
    };
  };

  const { isFetching: engagementLoading } = useGetSupplierEngagementReport(
    [filters?.year, filters?.year - 1],
    supplierId,
    {
      onError: (error) => {
        console.error("Error fetching supplier engagement report:", error);
        showErrorToast(toast, "Failed to fetch supplier engagement report", error.message);
      },
      onSuccess: (data) => {
        setCurrentYearEngagementData(prepareData(data?.[filters?.year]));
        setPreviousYearData(prepareData(data?.[filters?.year - 1]));
        setQQualityTag(data?.[filters?.year]?.yearlyEmission?.dataQuality || "N.A.");
      },
      cacheTime: 0,
      staleTime: 0,
      enabled: !!supplierId && !!filters?.year,
    }
  );

  const { data: engagementReport } = useGetSupplierEngagementReports(null, [supplierId], {
    enabled: !!supplierId && !!filters?.year,
    cacheTime: 0,
    staleTime: 0,
  });

  const closeSuppliersQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onSuppliersQuestionnaireClose();
  };

  const openSuppliersQuestionnaireModal = (questionnaireId) => {
    setSelectedQuestionnaireId(questionnaireId);
    onSuppliersQuestionnaireOpen();
  }

  useEffect(() => {
    const year = filters?.year;

    if (engagementReport && Object.keys(engagementReport)?.length > 0) {
      Object.keys(engagementReport).forEach((supplier) => {
        Object.keys(engagementReport[supplier]).forEach((username) => {
          const foundDecarbQuestionnaire = engagementReport[supplier][username].find(
            (questionnaire) => questionnaire.type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION
          );
          const foundGHGQuestionnaire = engagementReport[supplier][username].find(
            (questionnaire) =>
              questionnaire.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION && questionnaire.year === year
          );
          const momentDuration = moment.duration(foundDecarbQuestionnaire?.durationInMinutes, "minutes");
          if (foundDecarbQuestionnaire?.durationInMinutes) {
            setDecarbTag(`Decarbonisation: ${momentDuration.days()}d.`);
          }
          if (foundGHGQuestionnaire?.type) {
            setQTypeTag(foundGHGQuestionnaire.type);
          }
          if (foundGHGQuestionnaire?.status) {
            setQStatusTag(getStatusLabel(foundGHGQuestionnaire.status, null));
          }
          setGhgId(foundGHGQuestionnaire?.questionnaireId);
          setDecarbId(foundDecarbQuestionnaire?.questionnaireId);
        });
      });
    }
  }, [engagementReport, filters?.year]);

  return (
    <Page>
      <Flex direction="column" gap={2} mx="10%">
        <Flex onClick={() => navigate(routes.supplierInsightEmissionDetail)} align="center" gap={2} cursor="pointer">
          <ArrowLeft size={22} color="#85C22A" />
          <Text>Back</Text>
        </Flex>

        <H1>{currentYearEngagementData?.supplierName || "Loading"}</H1>

        <Text maxW="75%" color="gray.500" fontSize="body.m">
          Detail of supplier provided data.
        </Text>

        <H2 mt={8}>Supplier Attributable Emissions</H2>

        <Flex direction="column" gap={4}>
          <Flex alignSelf="end" gap={2}>
            <SupplierFiltersCatQualityYear filters={filters} setFilters={setFilters} showFilters={["year"]} />
          </Flex>

          <Flex gap={2}>
            <Badge bg="lightGreen" color="green.800" p={2} borderRadius="md" colorScheme="limeShade">{qQualityTag}</Badge>
            <Badge bg="status.orange" p={2} borderRadius="md" cursor="pointer" onClick={() => openSuppliersQuestionnaireModal(ghgId)} colorScheme="orange">{qTypeTag}</Badge>
            <Badge bg="status.pink" p={2} borderRadius="md" cursor="pointer" onClick={() => openSuppliersQuestionnaireModal(ghgId)} colorScheme="pink">{qStatusTag}</Badge>
            <Badge bg="limeShade.600" color="white" p={2} borderRadius="md" cursor="pointer" onClick={() => openSuppliersQuestionnaireModal(decarbId)}>{decarbTag}</Badge>
          </Flex>

          <SuppliersDataModal
            questionnaireId={selectedQuestionnaireId}
            isOpen={isSuppliersQuestionnaireOpen}
            onClose={closeSuppliersQuestionnaireModal}
          />

          <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6} align="stretch">
            <GridItem h="100%" minH="300px">
              <EmissionDetailCard
                isLoading={engagementLoading}
                title="Total"
                value={currentYearEngagementData?.totalCo2Emissions}
                secondValue={currentYearEngagementData?.totalAttributed}
                previousValue={previousYearData?.totalCo2Emissions}
              />
            </GridItem>
            <GridItem h="100%" minH="300px">
              <EmissionDetailCard
                isLoading={engagementLoading}
                title="Scope 1 & 2 Total"
                value={currentYearEngagementData?.scope1And2Total}
                secondValue={currentYearEngagementData?.scope1And2Attributed}
                previousValue={previousYearData?.scope1And2Total}
              />
            </GridItem>
            <GridItem h="100%" minH="300px">
              <EmissionDetailCard
                isLoading={engagementLoading}
                title="Scope 3"
                value={currentYearEngagementData?.scope3Total}
                secondValue={currentYearEngagementData?.scope3Attributed}
                previousValue={previousYearData?.scope3Total}
              />
            </GridItem>
            <GridItem h="100%" minH="300px">
              <SpendCard
                isLoading={engagementLoading}
                title="Spend"
                value={currentYearEngagementData?.spend}
                previousValue={previousYearData?.spend}
                secondValue={currentYearEngagementData?.revenueSpend}
                previousSecondValue={currentYearEngagementData?.revenueSpend}
              />
            </GridItem>
            <GridItem h="100%" minH="300px">
              <IntensityCard
                isLoading={engagementLoading}
                title="Intensity"
                value={currentYearEngagementData?.intensity}
                previousValue={previousYearData?.intensity}
              />
            </GridItem>
            <GridItem h="100%" minH="300px">
              <TargetDeltaCard
                isLoading={engagementLoading}
                title="Target Delta"
                value={currentYearEngagementData?.delta}
                previousValue={previousYearData?.delta}
              />
            </GridItem>
          </Grid>

          <IndividualSupplierTrends supplierId={supplierId} />
        </Flex>
      </Flex>
    </Page>
  );
};

const EmissionDetailCard = ({
  title,
  value,
  secondValue,
  previousValue,
  isLoading,
  showSecondValue = true,
  unit = "tCO2e",
}) => {
  let basePercentageChange = 0;
  if (previousValue && previousValue > 0) {
    basePercentageChange = ((value - previousValue) / previousValue) * 100;
  }

  return (
    <DashboardCard bg="#F7FAFC" px={8}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8}>
          <Box color="teal">
            <Heading py={2}>
              <Text as="span" fontSize="heading.xl" fontWeight="bold" color="gray.600">
                {getFormattedNumValue(value, 2, true) || 0}
              </Text>{" "}
              <Text as="span" fontSize="body.m" color="gray.600">
                {unit}
              </Text>
            </Heading>

            {showSecondValue && (
              <Heading py={2}>
                <Text as="span" fontSize="heading.2xl" fontWeight="bold" color="gray.800">
                  {getFormattedNumValue(secondValue, 2, true) || 0}
                </Text>{" "}
                <Text as="span" fontSize="body.m" color="gray.800">
                  {unit}
                </Text>
              </Heading>
            )}
          </Box>

          <Box>
            <Flex direction="row" width="100%" justifyContent="space-between">
              <Text
                display="flex"
                gap={1}
                color={basePercentageChange >= 0 ? "status.red" : "status.green"}
                fontSize="heading.s"
                fontWeight="bold"
              >
                {basePercentageChange >= 0 ? (
                  <TrendUp size={18} weight="bold" />
                ) : (
                  <TrendDown size={18} weight="bold" />
                )}
                {getFormattedNumValue(basePercentageChange, 2) || 0} %
              </Text>
            </Flex>
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

const SpendCard = ({ title, value, previousValue, secondValue, previousSecondValue, isLoading }) => {
  let firstBasePercentageChange = 0;
  if (previousValue && previousValue > 0) {
    firstBasePercentageChange = ((value - previousValue) / previousValue) * 100;
  }

  let secondBasePercentageChange = 0;
  if (previousSecondValue && previousSecondValue > 0) {
    secondBasePercentageChange = ((secondValue - previousSecondValue) / previousSecondValue) * 100;
  }

  return (
    <DashboardCard bg="#F7FAFC" px={8}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8}>
          <Box color="teal">
            <Heading py={2} display="flex" alignItems="center" gap={2}>
              <Text as="span" fontSize="heading.xl" fontWeight="bold" color="gray.600">
                {getFormattedNumValue(value, 2, true) || 0}
              </Text>{" "}
              <Text
                as="span"
                display="flex"
                alignItems="center"
                gap={1}
                color={firstBasePercentageChange >= 0 ? "status.red" : "status.green"}
                fontSize="heading.s"
                ml="auto"
                fontWeight="bold"
              >
                {firstBasePercentageChange >= 0 ? (
                  <TrendUp size={18} weight="bold" />
                ) : (
                  <TrendDown size={18} weight="bold" />
                )}
                {getFormattedNumValue(firstBasePercentageChange, 2) || 0} %
              </Text>
            </Heading>
            <Text color="gray.500">(Organization Provided)</Text>

            <Heading py={2} display="flex" alignItems="center" gap={2} mt={2}>
              <Text as="span" fontSize="heading.2xl" fontWeight="bold" color="gray.800">
                {getFormattedNumValue(secondValue, 2, true) || 0}
              </Text>{" "}
              <Text
                as="span"
                display="flex"
                alignItems="center"
                gap={1}
                color={secondBasePercentageChange >= 0 ? "status.red" : "status.green"}
                fontSize="heading.s"
                ml="auto"
                fontWeight="bold"
              >
                {secondBasePercentageChange >= 0 ? (
                  <TrendUp size={18} weight="bold" />
                ) : (
                  <TrendDown size={18} weight="bold" />
                )}
                {getFormattedNumValue(secondBasePercentageChange, 2) || 0} %
              </Text>
            </Heading>
            <Text color="gray.500">(Attributable Revenue)</Text>
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

const IntensityCard = ({ title, value, previousValue, isLoading }) => {
  let basePercentageChange = 0;
  if (previousValue && previousValue > 0) {
    basePercentageChange = ((value - previousValue) / previousValue) * 100;
  }

  return (
    <DashboardCard bg="#F7FAFC" px={8}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8}>
          <Box color="teal">
            <Heading py={2}>
              <Text as="span" fontSize="heading.xl" fontWeight="bold" color="gray.600">
                {getFormattedNumValue(value, 2, true) || 0}
              </Text>{" "}
              <Text as="span" fontSize="body.m" color="gray.600">
                tCO2e/£m
              </Text>
            </Heading>
            <Text color="gray.500">(Supplier Provided, All Scopes)</Text>
          </Box>

          <Box>
            <Flex direction="row" width="100%" justifyContent="space-between">
              <Text
                display="flex"
                gap={1}
                color={basePercentageChange >= 0 ? "status.red" : "status.green"}
                fontSize="heading.s"
                fontWeight="bold"
              >
                {basePercentageChange >= 0 ? (
                  <TrendUp size={18} weight="bold" />
                ) : (
                  <TrendDown size={18} weight="bold" />
                )}
                {getFormattedNumValue(basePercentageChange, 2) || 0} %
              </Text>
            </Flex>
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

const TargetDeltaCard = ({ title, value, previousValue, isLoading }) => {
  let basePercentageChange = 0;
  if (previousValue && previousValue > 0) {
    basePercentageChange = ((value - previousValue) / previousValue) * 100;
  }

  return (
    <DashboardCard bg="#F7FAFC" px={8}>
      <Text fontSize="heading.l" fontWeight="bold" color="teal">
        {title}
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8}>
          <Box color="teal">
            <Heading py={2}>
              <Text as="span" fontSize="heading.xl" fontWeight="bold" color="gray.600">
                {getFormattedNumValue(value, 2, true) || 0}
              </Text>{" "}
              <Text as="span" fontSize="body.m" color="gray.600">
                tCO2e/£m
              </Text>
            </Heading>
            <Text color="gray.500">(Supplier Provided, All Scopes)</Text>
          </Box>

          <Box>
            <Flex direction="row" width="100%" justifyContent="space-between">
              <Text
                display="flex"
                gap={1}
                color={basePercentageChange >= 0 ? "status.red" : "status.green"}
                fontSize="heading.s"
                fontWeight="bold"
              >
                {basePercentageChange >= 0 ? (
                  <TrendUp size={18} weight="bold" />
                ) : (
                  <TrendDown size={18} weight="bold" />
                )}
                {getFormattedNumValue(basePercentageChange, 2) || 0} %
              </Text>
            </Flex>
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

export default SupplierIndividualDetail;
